import React, { useState, useEffect } from 'react';
import { useTranslation } from '../i18n/LanguageContext';
import { guessQuoteAsset } from '../utils/symbol';

const LEVERAGE_PRESETS = [2, 3, 5, 10, 20, 50, 75, 100];

const STRATEGY_LABEL = {
  hma_adx: 'HMA+ADX', range_hma_adx: 'Range Bar+HMA+ADX',
  rsi50: 'RSI-50 Seviyesi', rsi_rsima: 'RSI+SMA',
  trend_pullback_r: 'Trend-Pullback + R', rsima50: 'SMA-50 Seviyesi',
};
// Açık Manuel İşlemler kartı dar olduğu için SL/TP'yi tam hassasiyetle (ör. 1.03750219) değil,
// büyüklüğüne göre makul basamak sayısıyla gösterir — Chart.jsx'teki fmtPrice ile aynı desen.
function fmtPrice(price) {
  const a = Math.abs(price);
  if (a >= 10000) return price.toFixed(1);
  if (a >= 1000)  return price.toFixed(2);
  if (a >= 10)    return price.toFixed(3);
  if (a >= 1)     return price.toFixed(4);
  if (a >= 0.01)  return price.toFixed(5);
  return price.toFixed(6);
}

const IS_HMA_FAMILY = strat => strat === 'range_hma_adx';
const IS_RSI_LIVE_FAMILY = strat => strat === 'rsi_rsima';
const USES_ATR_RISK = strat => IS_HMA_FAMILY(strat) || IS_RSI_LIVE_FAMILY(strat) || strat === 'trend_pullback_r' || strat === 'rsima50';

// exchangeStatus: { baseUrl, status: 'valid'|'invalid'|'missing' } | null — bkz. App.js'in
// GET /api/settings/exchange-status çağrısı. status yoksa ('missing', ilk yükleme) "-" gösterilir.
function exchangeLabel(exchangeStatus, t) {
  if (!exchangeStatus || exchangeStatus.status === 'missing') return t('botControl.activeExchangeNone');
  if (exchangeStatus.status === 'invalid') return t('botControl.activeExchangeInvalid');
  return exchangeStatus.baseUrl?.includes('tr.okx.com') ? 'OKX TR' : 'OKX Global';
}

function ActiveExchangeLine({ exchangeStatus, t }) {
  const withdrawEnabled = exchangeStatus?.status === 'withdraw_enabled';
  const needsHelp = !exchangeStatus || (exchangeStatus.status !== 'valid' && !withdrawEnabled);
  return (
    <>
      <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 6px' }}>
        {t('botControl.activeExchange')} : <strong style={{ color: 'var(--text)' }}>{exchangeLabel(exchangeStatus, t)}</strong>
        {needsHelp && (
          <span style={{ marginLeft: 5, cursor: 'help' }} title={t('botControl.activeExchangeHelpTooltip')}>ⓘ</span>
        )}
      </p>
      {withdrawEnabled && (
        <p style={{ fontSize: 12, color: '#dc2626', margin: '0 0 6px' }}>⚠️ {t('botControl.activeExchangeWithdrawWarning')}</p>
      )}
    </>
  );
}

function sessionMetaText(s) {
  return s.market === 'spot'
    ? `${s.quantity} ${guessQuoteAsset(s.symbol)} (Spot) · ${s.interval || '5m'}`
    : `${s.margin} USDT · ${s.leverage}x · ${s.interval || '5m'}`;
}

function SessionDetail({ s }) {
  const { t } = useTranslation();
  // Fallback YOK — s.strategy boş/tanınmıyorsa (ör. sunucunun henüz durdurmadığı yetim bir bot)
  // olduğu gibi bırakılır, aşağıdaki STRATEGY_LABEL[strat] || strat || '—' okunabilir gösterir.
  const strat = s.strategy;

  // Strateji kuralı satırları (her biri kendi satırında)
  let ruleLines = [];
  if (IS_HMA_FAMILY(strat)) {
    ruleLines = [
      `HMA:${s.hma_period}`,
      `ADX:${s.adx_period}`,
      s.adx_threshold > 0 ? t('botControl.ruleThresholdLabel', { v: s.adx_threshold }) : t('botControl.ruleThresholdOffLabel'),
    ];
    if (strat === 'range_hma_adx') ruleLines.push(`Box:${s.range_box_atr_mult}×ATR`);
  } else if (strat === 'rsi_rsima') {
    ruleLines = [t('botControl.ruleRsimaEntry'), t('botControl.ruleRsimaFlip')];
  } else if (strat === 'trend_pullback_r') {
    ruleLines = [
      t('botControl.ruleTrendPullback', { pb: s.pullback_atr_mult ?? 1.0, rt: s.rsi_trigger ?? 45 }),
      s.adx_threshold > 0 ? t('botControl.ruleAdxThresholdLabel', { v: s.adx_threshold }) : t('botControl.ruleAdxThresholdOffLabel'),
      t('botControl.ruleNoFixedTp'),
    ];
  } else if (strat === 'rsima50') {
    ruleLines = [
      t('botControl.ruleRsima50Entry', { dz: s.rsi_deadzone ?? 0 }),
      t('botControl.ruleRsima50Exit'),
    ];
  } else {
    if (s.atr_period != null) ruleLines.push(`ATR:${s.atr_period}`);
  }

  // Risk/koruma satırları (her biri kendi satırında)
  const riskLines = [];
  if (USES_ATR_RISK(strat) && s.atr_period != null) riskLines.push(`ATR:${s.atr_period}`);
  riskLines.push(s.sl_pct > 0 ? `SL:${s.sl_pct}%` : `SL:${s.atr_sl_mult}×ATR`);
  if (s.sl_cooldown_min != null) riskLines.push(t('botControl.riskCooldown', { v: s.sl_cooldown_min }));
  if ((IS_RSI_LIVE_FAMILY(strat) || strat === 'rsima50') && s.rsi_deadzone > 0) riskLines.push(t('botControl.riskDeadzone', { v: s.rsi_deadzone }));

  return (
    <div className="session-detail">
      <span className="session-strategy">{STRATEGY_LABEL[strat] || strat || '—'}</span>
      <div className="session-param-list">
        {ruleLines.map((line, i) => <div key={`r${i}`} className="session-param-line">{line}</div>)}
        {riskLines.map((line, i) => <div key={`k${i}`} className="session-param-line">{line}</div>)}
      </div>
    </div>
  );
}

export default function BotControl({ botState, onStop, isViewer, activeSymbol, subscription, userType, onTrace, tracedSymbols, userName, openTrades, onCloseTrade, exchangeStatus }) {
  const { t, lang } = useTranslation();
  const [symbol,   setSymbol]   = useState('BTCUSDT');
  const [market,   setMarket]   = useState('futures'); // 'futures' | 'spot'
  const [margin,   setMargin]   = useState('10');
  const [leverage, setLeverage] = useState(5);
  const [quantity, setQuantity] = useState('10');
  const [loading,  setLoading]  = useState(false);
  const [msg,      setMsg]      = useState(null);

  const sessions        = botState?.sessions || [];
  const hasSubscription = userType === 'system' || subscription === 'active';
  const isDemoMode      = userType === 'user'   && subscription !== 'active';
  const manualTrades    = (openTrades || []).filter(t => t.signal_type === 'manual');
  const [closingId, setClosingId] = useState(null);

  // OKX TR'de vadeli işlem piyasası yok (sadece spot) — bkz. tr.okx.com'un kendi ürün listesi.
  // Aktif borsa OKX TR ise Vadeli seçeneği devre dışı bırakılır, seçiliyken TR'ye geçilirse spot'a düşer.
  const isOkxTr = (exchangeStatus?.baseUrl || '').includes('tr.okx.com');
  useEffect(() => {
    if (isOkxTr && market === 'futures') setMarket('spot');
  }, [isOkxTr]); // eslint-disable-line

  const handleCloseManual = async (id) => {
    setClosingId(id);
    try {
      await onCloseTrade?.(id);
    } catch (err) {
      flash(err.message);
    } finally {
      setClosingId(null);
    }
  };

  useEffect(() => {
    if (activeSymbol) setSymbol(activeSymbol);
  }, [activeSymbol]);

  // Piyasa seçimine göre trace payload'ı — Spot'ta marjin/kaldıraç yerine miktar (USDT) gönderilir.
  const traceCfg = (sym) => market === 'spot'
    ? { symbol: sym, market, quantity: parseFloat(quantity) || 10 }
    : { symbol: sym, market, margin: parseFloat(margin) || 10, leverage };

  // Kaldıraç/marjin/miktar değiştiğinde aktif trace'i otomatik güncelle
  useEffect(() => {
    const s = symbol.trim().toUpperCase();
    const traced = (tracedSymbols || []).find(t => t.symbol === s);
    if (traced) onTrace?.(traceCfg(s));
  }, [leverage, margin, quantity, market]); // eslint-disable-line

  const flash = (text, type = 'error') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 5000);
  };

  const handleStop = async (sym) => {
    setLoading(true);
    try {
      await onStop(sym);
      flash(t('botControl.stopSuccess', { symbol: sym }), 'success');
    } catch (err) {
      flash(err.message);
    } finally {
      setLoading(false);
    }
  };

  const MAX_PAIRS    = 4;
  const sym          = symbol.trim().toUpperCase();
  const alreadyRunning = sessions.some(s => s.symbol === sym);
  const isTraced     = (tracedSymbols || []).some(t => t.symbol === sym);

  const trackedSet   = new Set([
    ...sessions.map(s => s.symbol),
    ...(tracedSymbols || []).map(t => t.symbol),
  ]);
  const limitReached = trackedSet.size >= MAX_PAIRS && !trackedSet.has(sym);

  if (isViewer) return (
    <div className="bot-control">
      {userName && (
        <>
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 2px' }}>
            {t('botControl.greeting')} <strong style={{ color: 'var(--text)' }}>{t('botControl.greetingName', { name: userName })}</strong>
          </p>
          <ActiveExchangeLine exchangeStatus={exchangeStatus} t={t} />
        </>
      )}
      <p className="card-title">{t('botControl.title')}</p>
      <div className="viewer-badge">{t('botControl.viewerBadge')}</div>
      <div style={{ marginTop: 16 }}>
        {sessions.length === 0 ? (
          <p style={{ color: 'var(--muted)', fontSize: 13 }}>{t('botControl.noActiveBots')}</p>
        ) : (
          <>
            <p className="card-title" style={{ fontSize: 12, marginBottom: 8 }}>{t('botControl.activeBots')}</p>
            <div className="active-sessions">
              {sessions.map(s => (
                <div key={s.symbol} className="session-row">
                  <div>
                    <span className="session-symbol" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      {s.symbol}
                      {s.is_test && (
                        <span style={{
                          fontSize: 9, fontWeight: 700,
                          background: 'rgba(161,97,210,0.15)', color: '#a161d2',
                          border: '1px solid rgba(161,97,210,0.5)',
                          borderRadius: 3, padding: '1px 5px',
                        }}>TEST</span>
                      )}
                    </span>
                    <span className="session-meta">{sessionMetaText(s)}</span>
                    <SessionDetail s={s} />
                  </div>
                  <span className="session-status">{t('botControl.running')}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="bot-control">
      {userName && (
        <>
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 2px' }}>
            {t('botControl.greeting')} <strong style={{ color: 'var(--text)' }}>{t('botControl.greetingName', { name: userName })}</strong>
          </p>
          <ActiveExchangeLine exchangeStatus={exchangeStatus} t={t} />
        </>
      )}
      <p className="card-title">{t('botControl.title')}</p>

      {/* Piyasa */}
      <div className="form-group">
        <label>{t('botControl.market')}</label>
        <div className="leverage-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <button
            className={`leverage-btn ${market === 'spot' ? 'active' : ''}`}
            onClick={() => setMarket('spot')}
          >
            {t('botControl.spot')}
          </button>
          <button
            className={`leverage-btn ${market === 'futures' ? 'active' : ''}`}
            onClick={() => setMarket('futures')}
            disabled={isOkxTr}
            title={isOkxTr ? t('botControl.futuresDisabledOkxTr') : undefined}
          >
            {t('botControl.futures')}
          </button>
        </div>
        {isOkxTr && (
          <p style={{ fontSize: 11, color: 'var(--muted)', margin: '6px 0 0' }}>
            ⚠ {t('botControl.futuresDisabledOkxTr')}
          </p>
        )}
      </div>

      {/* Parite */}
      <div className="form-group">
        <label>{t('botControl.pair')}</label>
        <input
          className="input"
          placeholder={t('botControl.pairPlaceholder')}
          value={symbol}
          onChange={e => setSymbol(e.target.value.toUpperCase())}
        />
      </div>

      {market === 'spot' ? (
        /* Miktar (Spot) */
        <div className="form-group">
          <label>{t('botControl.quantity')}</label>
          <div className="input-suffix">
            <input
              className="input"
              type="number"
              min="1"
              step="1"
              placeholder="10"
              value={quantity}
              onChange={e => setQuantity(e.target.value)}
            />
            <span className="suffix-label">{guessQuoteAsset(symbol)}</span>
          </div>
        </div>
      ) : (
        <>
          {/* Marjin */}
          <div className="form-group">
            <label>{t('botControl.margin')}</label>
            <div className="input-suffix">
              <input
                className="input"
                type="number"
                min="1"
                step="1"
                placeholder="10"
                value={margin}
                onChange={e => setMargin(e.target.value)}
              />
              <span className="suffix-label">USDT</span>
            </div>
          </div>

          {/* Kaldıraç */}
          <div className="form-group">
            <label>{t('botControl.leverageLabel', { leverage })}</label>
            <div className="leverage-grid">
              {LEVERAGE_PRESETS.map(lv => (
                <button
                  key={lv}
                  className={`leverage-btn ${leverage === lv ? 'active' : ''}`}
                  onClick={() => setLeverage(lv)}
                >
                  {lv}x
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      <hr className="divider" />

      {/* Demo mod / abonelik uyarısı */}
      {isDemoMode && (
        <div className="subscription-required" style={{ borderColor: 'rgba(161,97,210,0.4)', background: 'rgba(161,97,210,0.06)' }}>
          <span className="sub-req-icon">⚗</span>
          <div style={{ flex: 1 }}>
            <div className="sub-req-title" style={{ color: '#a161d2' }}>{t('botControl.demoModeTitle')}</div>
            <div className="sub-req-text">
              {t('botControl.demoModeText')}
            </div>
          </div>
        </div>
      )}

      {/* Çalışıyor göstergesi — ayrı, butonu gizlemez */}
      {alreadyRunning && (
        <div className="running-info">
          <span className="running-dot" /> {t('botControl.runningInfo', { symbol: sym })}
        </div>
      )}

      {/* Takip Et butonu — demo modda da çalışır */}
      <button
        className={`btn btn-trace${isTraced ? ' active' : ''}`}
        disabled={(!hasSubscription && !isDemoMode) || limitReached}
        onClick={() => onTrace?.(traceCfg(sym))}
        title={
          limitReached
            ? t('botControl.maxPairsTitle', { max: MAX_PAIRS })
            : isTraced
              ? t('botControl.updateTraceTitle')
              : t('botControl.startTraceTitle')
        }
      >
        {t('botControl.traceButton')}
      </button>

      {/* Abone Ol butonu — aboneliği aktif olmayan kullanıcılar için, ürün sayfasını yeni sekmede açar */}
      {isDemoMode && (
        <button
          className="sub-req-btn"
          style={{ width: '100%', marginTop: 8, height: 40 }}
          onClick={() => window.open(
            `https://uygarteknoloji.com.tr/urunler/detay.php?slug=https%3A%2F%2Ftrendsniper.com.tr&lang=${lang}`,
            '_blank',
            'noopener,noreferrer'
          )}
        >
          {t('botControl.subscribeButton')}
        </button>
      )}

      {/* Mesaj */}
      {msg && (
        <div className={`msg ${msg.type === 'success' ? 'msg-success' : 'msg-error'}`}>
          {msg.text}
        </div>
      )}

      {/* Aktif Botlar */}
      {sessions.length > 0 && (
        <>
          <hr className="divider" />
          <p className="card-title">{t('botControl.activeBots')}</p>
          <div className="session-list">
            {sessions.map(s => (
              <div key={s.symbol} className={`session-item${s.is_test ? ' session-item-test' : ''}`}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="session-symbol" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {s.symbol}
                    {s.is_test && (
                      <span style={{
                        fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
                        background: 'rgba(161,97,210,0.15)', color: '#a161d2',
                        border: '1px solid rgba(161,97,210,0.5)',
                        borderRadius: 3, padding: '1px 5px',
                      }}>TEST</span>
                    )}
                  </div>
                  <div className="session-meta">{sessionMetaText(s)}</div>
                  <SessionDetail s={s} />
                </div>
                <button
                  className="session-stop"
                  onClick={() => handleStop(s.symbol)}
                  disabled={loading}
                >
                  {t('botControl.stopButton')}
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Açık Manuel İşlemler — bot'tan bağımsız, kullanıcının kendi açtığı işlemler.
          Hiçbir strateji/periyot bağımlılığı yok; TP/SL sadece açılışta belirlenen (ya da grafikte
          sürüklenerek sonradan değiştirilen) sabit değerdir — bkz. botManager.js _checkDynamicTpAndClose
          (signal_type==='manual' olan işlemler dinamik/strateji-bazlı çıkıştan tamamen muaf tutulur). */}
      {manualTrades.length > 0 && (
        <>
          <hr className="divider" />
          <p className="card-title">{t('botControl.openManualTrades')}</p>
          <div className="session-list">
            {manualTrades.map(trade => (
              <div key={trade.id} className={`session-item${trade.is_test ? ' session-item-test' : ''}`}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="session-symbol" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {trade.symbol}
                    {/* Spot'ta yön kavramı yok (her zaman side='long' veri modeli gereği, kullanıcıya
                        gösterilecek bir "LONG" anlamı taşımaz) — bu yüzden rozet sadece vadelide gösterilir. */}
                    {trade.market !== 'spot' && (
                      <span style={{
                        fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
                        background: trade.side === 'long' ? 'rgba(22,163,74,0.15)' : 'rgba(220,38,38,0.15)',
                        color: trade.side === 'long' ? '#16a34a' : '#dc2626',
                        border: `1px solid ${trade.side === 'long' ? 'rgba(22,163,74,0.5)' : 'rgba(220,38,38,0.5)'}`,
                        borderRadius: 3, padding: '1px 5px',
                      }}>{trade.side?.toUpperCase()}</span>
                    )}
                    {trade.is_test && (
                      <span style={{
                        fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
                        background: 'rgba(161,97,210,0.15)', color: '#a161d2',
                        border: '1px solid rgba(161,97,210,0.5)',
                        borderRadius: 3, padding: '1px 5px',
                      }}>TEST</span>
                    )}
                  </div>
                  <div className="session-meta">
                    {trade.market === 'spot'
                      ? `${t('botControl.quantity')}: ${parseFloat(trade.quantity).toFixed(2)} USDT`
                      : `${t('botControl.margin')}: ${parseFloat(trade.margin).toFixed(2)} USDT · ${trade.leverage}x`}
                  </div>
                  <div className="session-meta">
                    {trade.market === 'spot' ? t('botControl.spot') : t('botControl.futures')} · {t('botControl.entryLabel')}: {fmtPrice(parseFloat(trade.entry_price))}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                    SL: <strong style={{ color: '#dc2626' }}>{parseFloat(trade.sl_price) ? fmtPrice(parseFloat(trade.sl_price)) : t('botControl.slNone')}</strong>
                    {' · '}
                    TP: <strong style={{ color: '#10b981' }}>{parseFloat(trade.tp_price) > 0 ? fmtPrice(parseFloat(trade.tp_price)) : t('botControl.tpNone')}</strong>
                  </div>
                </div>
                <button
                  className="session-stop"
                  onClick={() => handleCloseManual(trade.id)}
                  disabled={closingId === trade.id}
                >
                  {closingId === trade.id ? '…' : (trade.market === 'spot' ? t('botControl.closeButtonSpot') : t('botControl.closeButton'))}
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
